import { Controller } from "@hotwired/stimulus";

// Controller: respondent-modals
// Automatically shows the answer submitted modal when it is appended via Turbo Stream.
// Also provides showModeratorAlert() method for ControlChannel alert notifications.
// Works with Bootstrap 4/5 (if present); falls back to simple display if modal plugin unavailable.
export default class extends Controller {
  connect() {
    // Show any existing modal on connect (in case of race / cache restore)
    this.showLatestModal();

    // Observe future modal insertions
    this.observer = new MutationObserver(() => this.showLatestModal());
    this.observer.observe(this.element, { childList: true });

    // Expose globally for ControlChannel to call
    window.showModeratorAlert = this.showModeratorAlert.bind(this);
    console.log('respondent-modals controller connected, showModeratorAlert exposed');
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    // Clean up global reference
    if (window.showModeratorAlert) {
      delete window.showModeratorAlert;
    }
  }

  showLatestModal() {
    try {
      const modalEl = this.element.querySelector('#answer-submitted-modal');
      if (!modalEl) return;
      // If already visible, skip
      if (modalEl.classList.contains('show')) return;
      // Prefer Bootstrap's modal plugin if available
      if (window.bootstrap && window.bootstrap.Modal) {
        const instance = bootstrap.Modal.getOrCreateInstance(modalEl, { backdrop: 'static', keyboard: true });
        instance.show();
      } else if (typeof window.$ === 'function' && window.$(modalEl).modal) {
        window.$(modalEl).modal('show');
      } else {
        // Fallback: emulate basic modal display
        modalEl.style.display = 'block';
        modalEl.classList.add('show');
        modalEl.removeAttribute('aria-hidden');
      }
    } catch (e) {
      // Non-fatal
      console.warn('respondent-modals showLatestModal failed', e);
    }
  }

  /**
   * Display a moderator alert message to the user.
   * Manages accessibility: sets page content inert, traps focus within overlay,
   * and handles Escape key for dismissible alerts.
   *
   * @param {string} message - The alert message to display
   * @param {string} kind - The type of alert (alert_only, alert_and_go_to_question, alert_and_end_survey)
   */
  showModeratorAlert(message, kind = 'alert_only') {
    try {
      // Use the existing notification-overlay in the layout
      const alertOverlay = document.getElementById('alert');
      const messageEl = document.getElementById('alert-modal-message');
      const buttonEl = document.getElementById('alert-button-action');
      const subMessageEl = document.getElementById('alert-modal-sub-message');

      // Elements to make inert (everything outside the overlay)
      const mainContent = document.getElementById('main-content');
      const skipLink = document.querySelector('.skip-link');
      const helpButton = document.getElementById('help-button');
      const navbar = document.querySelector('.navbar');
      const sidebar = document.querySelector('.app__sidebar');

      if (!alertOverlay || !messageEl || !buttonEl) {
        console.warn('Notification overlay not found, falling back to browser alert');
        alert(`Moderator Alert: ${message}`);
        if (kind === 'alert_and_go_to_question' || kind === 'alert_and_end_survey') {
          window.location.reload();
        }
        return;
      }

      // Update the message
      messageEl.textContent = message;

      // Set sub-message based on alert type
      if (subMessageEl) {
        if (kind === 'alert_and_go_to_question') {
          subMessageEl.textContent = 'By clicking "I Understand" you\'ll be taken to the new current question.';
        } else if (kind === 'alert_and_end_survey') {
          subMessageEl.textContent = 'If you need help or believe an error has occurred, please contact an ACUPOLL Admin.';
        } else {
          subMessageEl.textContent = 'You may continue finishing your answers.';
        }
      }

      // Accessibility: Set all background elements inert to trap focus in overlay
      const inertElements = [mainContent, skipLink, helpButton, navbar, sidebar].filter(el => el);
      inertElements.forEach(el => el.setAttribute('inert', ''));

      // Show the overlay
      alertOverlay.classList.remove('d-none');

      // Accessibility: Move focus to the button for keyboard users
      buttonEl.focus();

      // Handle dismiss - button click or Escape key (for alert_only)
      const handleDismiss = () => {
        alertOverlay.classList.add('d-none');
        buttonEl.removeEventListener('click', handleDismiss);
        document.removeEventListener('keydown', handleKeydown);

        // Accessibility: Remove inert from all elements
        inertElements.forEach(el => el.removeAttribute('inert'));

        if (kind === 'alert_and_go_to_question' || kind === 'alert_and_end_survey') {
          window.location.reload();
        }
      };

      // Handle Escape key for dismissible alerts (alert_only type)
      const handleKeydown = (e) => {
        if (e.key === 'Escape' && kind === 'alert_only') {
          handleDismiss();
        }
      };

      buttonEl.addEventListener('click', handleDismiss);
      document.addEventListener('keydown', handleKeydown);
    } catch (e) {
      console.error('Failed to show moderator alert:', e);
      // Fallback to browser alert
      alert(`Moderator Alert: ${message}`);
      if (kind === 'alert_and_go_to_question' || kind === 'alert_and_end_survey') {
        window.location.reload();
      }
    }
  }
}
